/**
 * JWEEncryptor — encrypts card data using Web Crypto API.
 *
 * Uses RSA-OAEP key agreement and A256GCM content encryption.
 * Accepts public key as JWK, produces JWE compact serialisation string.
 * Raw card data never leaves the iframe unencrypted.
 *
 * Requirements: 8.6, 14.1, 14.4
 */

export interface CardDataPayload {
  cardNumber: string;
  expiry: string;
  cvc: string;
}

/**
 * Base64url encode a Uint8Array.
 */
function base64urlEncode(data: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < data.length; i++) {
    binary += String.fromCharCode(data[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export const JWEEncryptor = {
  /**
   * Encrypt card data into a JWE compact serialisation string.
   *
   * JWE Compact Serialization: BASE64URL(Header).BASE64URL(EncryptedKey).BASE64URL(IV).BASE64URL(Ciphertext).BASE64URL(Tag)
   */
  async encrypt(cardData: CardDataPayload, publicKeyJwk: JsonWebKey): Promise<string> {
    // Import the RSA-OAEP public key
    const publicKey = await crypto.subtle.importKey(
      "jwk",
      publicKeyJwk,
      { name: "RSA-OAEP", hash: "SHA-256" },
      false,
      ["wrapKey"]
    );

    // Generate a random AES-256-GCM content encryption key (CEK)
    const cek = await crypto.subtle.generateKey(
      { name: "AES-GCM", length: 256 },
      true,
      ["encrypt"]
    );

    // Wrap (encrypt) the CEK with the RSA-OAEP public key
    const wrappedKey = new Uint8Array(
      await crypto.subtle.wrapKey("raw", cek, publicKey, { name: "RSA-OAEP" })
    );

    // Generate a random 96-bit IV for AES-GCM
    const iv = crypto.getRandomValues(new Uint8Array(12));

    // Build the JWE protected header
    const header = {
      alg: "RSA-OAEP-256",
      enc: "A256GCM",
      kid: (publicKeyJwk as Record<string, unknown>).kid as string,
    };
    const headerStr = JSON.stringify(header);
    const encodedHeader = base64urlEncode(new TextEncoder().encode(headerStr));

    // Additional Authenticated Data (AAD) is the encoded header
    const aad = new TextEncoder().encode(encodedHeader);

    // Encrypt the card data payload
    const plaintext = new TextEncoder().encode(JSON.stringify(cardData));
    const encrypted = await crypto.subtle.encrypt(
      { name: "AES-GCM", iv, additionalData: aad, tagLength: 128 },
      cek,
      plaintext
    );

    // AES-GCM appends the auth tag to the ciphertext
    const encryptedBytes = new Uint8Array(encrypted);
    const ciphertext = encryptedBytes.slice(0, encryptedBytes.length - 16);
    const tag = encryptedBytes.slice(encryptedBytes.length - 16);

    // Build JWE compact serialisation
    return [
      encodedHeader,
      base64urlEncode(wrappedKey),
      base64urlEncode(iv),
      base64urlEncode(ciphertext),
      base64urlEncode(tag),
    ].join(".");
  },
};
